import React, { useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
} from "react-router-dom";
import { Layout, Menu } from "antd";
import { useMediaQuery } from "react-responsive";
import {
  CreditCardOutlined,
  UserOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";

import LandingPage       from "./LandingPage";
import PublicHome        from "./PublicHome";
import BottomNavigation  from "./BottomNavigation";
import RegisterUser      from "./RegisterUser.tsx";
import ProfilePage       from "./ProfilePage";
import UnlinkCardPage    from "./UnlinkCardPage";
import ChangePlanPage    from "./ChangePlanPage";
import SubscriptionPage  from "./SubscriptionPage";
import DownloadsPage     from "./download/DownloadsPage";
import SocialDownloaderPage from "./pages/SocialDownloaderPage.tsx";
import RedirectHandlerV2 from "./RedirectHandler_v2";
import DeleteAccountPage from "./DeleteAccountPage";
import "./App.css";

/* новые разделы */
import TasksPage  from "./pages/TasksPage.tsx";
import NotesPage  from "./pages/NotesPage.tsx";
import GraphPage  from "./pages/GraphPage.tsx"

import { useApp } from "./context/AppContext.tsx"
import { getSubscriptionStatus } from "./api_v2/subscription";
import { getToken } from "./api_v2/client";

const { Content, Sider } = Layout;

function SubscriptionOnlyRoute({ children }: { children: React.ReactElement }) {
  const { user, ready } = useApp();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    setAllowed(null);
    setRedirectTo(null);
    if (!user?.id || !getToken()) {
      setRedirectTo("/register");
      return;
    }

    let cancelled = false;
    getSubscriptionStatus()
      .then((data) => {
        if (cancelled) return;
        const active = data?.success && data?.subscription_status === "active";
        if (active) {
          setAllowed(true);
        } else {
          setRedirectTo("/subscription");
        }
      })
      .catch(() => {
        if (cancelled) return;
        setRedirectTo("/subscription");
      });

    return () => {
      cancelled = true;
    };
  }, [ready, user?.id]);

  if (!ready || (allowed === null && !redirectTo)) {
    return <div style={{ textAlign: "center", padding: 24 }}>Проверка доступа...</div>;
  }
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
}

/* ─────────────── тип меню ─────────────── */
interface MenuItemCfg {
  key: string;
  icon: React.ReactNode;
  label: React.ReactNode;
  protected?: boolean; // отображать только залогиненному
  subscriptionOnly?: boolean;
}

/* ─────────────── конфиг меню ─────────────── */
const groupedMenuCfg: { title: string; items: MenuItemCfg[] }[] = [
  {
    title: "Основное",
    items: [
      { key: "/subscription", icon: <CreditCardOutlined />, label: "Подписка" },
      { key: "/links", icon: <AppstoreOutlined />, label: "Приложения", protected: true, subscriptionOnly: true },
      { key: "/profile", icon: <UserOutlined />, label: "Профиль" },
    ],
  },
];


/* ─────────────── основной компонент ─────────────── */
export default function App() {
  const { user, setUser, ready } = useApp();
  const location = useLocation();

  /* mobile‑breakpoint */
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [hasActiveSubscription, setHasActiveSubscription] = useState(
    user?.subscription_status === "active"
  );

  /* авторизован ли пользователь */
  const isRegistered = !!user;

  const query = new URLSearchParams(location.search);
  const hasToken = query.has("token");
  const hasSimolSession = query.has("session_token");
  const shouldShowBottomNav =
    isMobile && isRegistered && location.pathname !== "/register";

  useEffect(() => {
    if (!user?.id || !getToken()) {
      setHasActiveSubscription(false);
      return;
    }

    let cancelled = false;
    getSubscriptionStatus()
      .then(data => {
        if (!cancelled) {
          setHasActiveSubscription(Boolean(data?.success && data?.subscription_status === "active"));
        }
      })
      .catch(() => {
        if (!cancelled) setHasActiveSubscription(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id, location.pathname]);

  /* генерируем меню динамически */
const renderMenuItems = () =>
  groupedMenuCfg.map(group => (
    <Menu.ItemGroup key={group.title} title={group.title}>
      {group.items
        .filter(item => (item.protected ? isRegistered : true))
        .filter(item => (item.subscriptionOnly ? hasActiveSubscription : true))
        .map(({ key, icon, label }) => (
          <Menu.Item key={key} icon={icon}>
            <Link to={key}>{label}</Link>
          </Menu.Item>
        ))}
    </Menu.ItemGroup>
  ));


  /* хендл логина из RedirectHandler */
const handleLogin = async (u: { id: number; name: string; hasSubscription?: boolean }) => {
  setUser({
    id: u.id,
    name: u.name,
    hasSubscription: u.hasSubscription,
  });
};

  const rootElement = hasToken ? (
    <RedirectHandlerV2 onLogin={handleLogin} />
  ) : hasSimolSession ? (
    <RegisterUser />
  ) : !ready ? (
    <div style={{ textAlign: "center", padding: 24 }}>Загрузка...</div>
  ) : isRegistered ? (
    <Navigate to="/subscription" replace />
  ) : (
    <PublicHome />
  );
  /* ─────────── UI ─────────── */
  return (
    <Layout className="app-shell">
      {/* ==== DESKTOP SIDEBAR ==== */}
      {!isMobile && (
        <Sider width={238} theme="light" className="app-sidebar">
          <Link to="/subscription" className="app-brand" aria-label="Simol Apps — подписка">
            <img src="/logo192.png" alt="" />
            <span>
              <strong>SIMOL</strong>
              <small>apps & vpn</small>
            </span>
          </Link>
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={[location.pathname]}
            className="app-sidebar__menu"
          >
            {renderMenuItems()}
          </Menu>
        </Sider>
      )}

      {/* ==== MAIN CONTENT ==== */}
      <Layout className="app-main">
        <Content
          className={`app-content${shouldShowBottomNav ? " mobile-app-content mobile-app-content--with-tabs" : ""}`}
        >
          <Routes>
            {/* точка входа (получаем токен, логиним) */}
            <Route path="/" element={rootElement} />

            <Route path="/register" element={<RegisterUser />} />

            {/* базовые страницы */}
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/profile"      element={<ProfilePage />} />
            <Route
              path="/links"
              element={(
                <SubscriptionOnlyRoute>
                  <LandingPage />
                </SubscriptionOnlyRoute>
              )}
            />

            {/* новые: задачи, заметки, граф */}
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/notes" element={<NotesPage />} />
            <Route path="/graph" element={<GraphPage />} />

            {/* защищённые разделы */}
            <Route path="/unlink-card"    element={<UnlinkCardPage />} />
            <Route path="/change-plan"    element={<ChangePlanPage />} />
            <Route
              path="/downloads/*"
              element={(
                <SubscriptionOnlyRoute>
                  <DownloadsPage />
                </SubscriptionOnlyRoute>
              )}
            />
            <Route
              path="/social-download"
              element={(
                <SubscriptionOnlyRoute>
                  <SocialDownloaderPage />
                </SubscriptionOnlyRoute>
              )}
            />
            <Route path="/delete-account" element={<DeleteAccountPage />} />

            {/* fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Content>
      </Layout>
      {shouldShowBottomNav && <BottomNavigation showApps={hasActiveSubscription} />}
    </Layout>
  );
}
